import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Crown, Loader2, CreditCard } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import { useToast } from '@/components/ui/use-toast';
import { format } from 'date-fns';
import { openPaddleCheckout, paddleConfig } from '@/lib/paddle';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export default function Subscription() {
  const { user, userProfile, refreshUserProfile } = useAuth();
  const { isPro, currentPeriodEnd, subscriptionStatus } = useSubscription();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);

  // Handle successful payment callback with polling
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const provider = urlParams.get('provider');

    if (success === 'true' && provider) {
      // Show success message immediately
      toast({
        title: 'Payment Successful!',
        description: 'Activating your subscription...',
      });

      // Poll for subscription update (check every 2 seconds for up to 30 seconds)
      let attempts = 0;
      const maxAttempts = 15;
      const pollInterval = setInterval(async () => {
        attempts++;
        await refreshUserProfile();
        
        // Check if subscription is now active
        if (userProfile?.plan === 'pro' && userProfile?.subscription?.status === 'active') {
          clearInterval(pollInterval);
          toast({
            title: 'Subscription Activated!',
            description: 'You now have access to all Pro features.',
          });
          // Clean up URL after successful activation
          window.history.replaceState({}, '', window.location.pathname);
        } else if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
          toast({
            title: 'Subscription Pending',
            description: 'Your payment was successful. Subscription will be activated shortly. Please refresh the page in a moment.',
            variant: 'default',
          });
          // Clean up URL even if not activated yet
          window.history.replaceState({}, '', window.location.pathname);
        }
      }, 2000);

      // Cleanup interval on unmount
      return () => clearInterval(pollInterval);
    }
  }, [refreshUserProfile, toast, userProfile?.plan, userProfile?.subscription?.status]);

  const handleUpgrade = async () => {
    // Directly open Paddle checkout (Stripe hidden for now)
    handlePaddleCheckout();
  };

  const handleStripeCheckout = async () => {
    if (!user) return;

    setLoading(true);
    setShowPaymentDialog(false);
    try {
      const createCheckoutSession = httpsCallable(functions!, 'createCheckoutSession');
      const result = await createCheckoutSession() as { data: { url: string } };
      
      if (result.data.url) {
        window.location.href = result.data.url;
      }
    } catch (error: any) {
      console.error('Error creating checkout session:', error);
      toast({
        title: 'Error',
        description: 'Failed to start checkout process. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePaddleCheckout = async () => {
    if (!user || !userProfile) return;

    setLoading(true);
    setShowPaymentDialog(false);
    try {
      await openPaddleCheckout(
        paddleConfig.products.pro.monthly,
        user.uid,
        userProfile.email,
        () => {
          toast({
            title: 'Success!',
            description: 'Your subscription has been activated.',
          });
        },
        (error) => {
          console.error('Paddle checkout error:', error);
          toast({
            title: 'Error',
            description: 'Failed to complete checkout. Please try again.',
            variant: 'destructive',
          });
        }
      );
    } catch (error: any) {
      console.error('Error opening Paddle checkout:', error);
      toast({
        title: 'Error',
        description: 'Failed to start checkout process. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    if (!user || !userProfile) return;

    // Check which payment provider the user is using
    const provider = userProfile.subscription?.provider;

    setLoading(true);
    try {
      if (provider === 'stripe') {
        // Open Stripe Customer Portal
        const createPortalSession = httpsCallable(functions!, 'createPortalSession');
        const result = await createPortalSession() as { data: { url: string } };
        
        if (result.data.url) {
          window.location.href = result.data.url;
        }
      } else if (provider === 'paddle') {
        // For Paddle, call cancel function
        console.log('Before cancel - currentPeriodEnd:', currentPeriodEnd);
        console.log('Before cancel - userProfile:', userProfile);
        
        const cancelSubscription = httpsCallable(functions!, 'cancelPaddleSubscription');
        const result = await cancelSubscription() as { data: { success: boolean; message: string } };
        
        if (result.data.success) {
          toast({
            title: 'Subscription Cancelled',
            description: result.data.message,
          });
          // Wait a moment for Firestore to update, then refresh
          await new Promise(resolve => setTimeout(resolve, 1000));
          await refreshUserProfile();
          
          // Log after refresh
          setTimeout(() => {
            console.log('After cancel - currentPeriodEnd:', currentPeriodEnd);
            console.log('After cancel - userProfile:', userProfile);
          }, 500);
        }
      } else {
        toast({
          title: 'Error',
          description: 'Unable to determine subscription provider.',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Error managing subscription:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to manage subscription. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      <h1 className="font-heading text-3xl font-bold mb-2">Subscription</h1>
      <p className="text-muted-foreground mb-8">
        Manage your subscription and billing
      </p>

      {/* Current Plan Status */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Current Plan</CardTitle>
          <CardDescription>Your subscription details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-2xl font-bold flex items-center gap-2">
                  {isPro && <Crown className="h-6 w-6 text-yellow-500" />}
                  {isPro ? 'Pro Plan' : 'Free Plan'}
                </p>
                {isPro && userProfile?.subscription?.scheduledCancellation && userProfile?.subscription?.cancelAt && (
                  <Badge variant="destructive" className="text-xs">
                    Cancels {format(userProfile.subscription.cancelAt, 'MMM dd, yyyy')}
                  </Badge>
                )}
              </div>
              {isPro && (
                <p className="text-sm text-muted-foreground mt-1">
                  {userProfile?.subscription?.scheduledCancellation && userProfile?.subscription?.cancelAt && `Pending Cancellation on ${format(userProfile.subscription.cancelAt, 'MMMM dd, yyyy')}`}
                  {userProfile?.subscription?.scheduledCancellation && !userProfile?.subscription?.cancelAt && currentPeriodEnd && `Pending Cancellation on ${format(currentPeriodEnd, 'MMMM dd, yyyy')}`}
                  {!userProfile?.subscription?.scheduledCancellation && currentPeriodEnd && subscriptionStatus === 'active' && `Renews on ${format(currentPeriodEnd, 'MMMM dd, yyyy')}`}
                  {!userProfile?.subscription?.scheduledCancellation && currentPeriodEnd && subscriptionStatus === 'cancelled' && `Access until ${format(currentPeriodEnd, 'MMMM dd, yyyy')}`}
                </p>
              )}
            </div>
            {isPro && subscriptionStatus === 'active' && !userProfile?.subscription?.scheduledCancellation ? (
              <Button variant="outline" onClick={handleManageSubscription} disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {userProfile?.subscription?.provider === 'paddle' ? 'Cancel Subscription' : 'Manage Subscription'}
              </Button>
            ) : null}
          </div>

        </CardContent>
      </Card>

      {/* Pricing Plans */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Free Plan */}
        <Card className={!isPro ? 'border-primary' : ''}>
          <CardHeader>
            <CardTitle className="text-2xl">Free</CardTitle>
            <div className="mt-4">
              <span className="text-4xl font-bold">$0</span>
              <span className="text-muted-foreground">/month</span>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Unlimited invoice & quotation downloads</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Manual business entry each time</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Manual customer entry each time</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Manual item entry each time</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Save unlimited invoices &amp; quotes</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Quotations &amp; Estimates (manual)</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span>PDF export</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Standard support</span>
              </li>
            </ul>

            {!isPro ? (
              <Badge variant="outline" className="w-full justify-center py-2">
                Current Plan
              </Badge>
            ) : null}
          </CardContent>
        </Card>

        {/* Pro Plan */}
        <Card className={isPro ? 'border-primary' : 'border-2 border-primary'}>
          {!isPro && (
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <Badge className="px-4 py-1 flex items-center gap-1">
                <Crown className="h-4 w-4" />
                Most Popular
              </Badge>
            </div>
          )}
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              Pro
              <Crown className="h-5 w-5 text-yellow-500" />
            </CardTitle>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="text-xl text-muted-foreground line-through font-semibold">$15</span>
              <span className="text-4xl font-bold text-slate-900">$5</span>
              <span className="text-muted-foreground">/month</span>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="font-medium">Unlimited invoice & quotation downloads</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="font-medium">Save unlimited business profiles</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="font-medium">Business logo upload & custom branding</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="font-medium">Save unlimited customer profiles</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="font-medium">Save unlimited inventory items & services (search & auto-fill)</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="font-medium">Save unlimited invoices &amp; quotes</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="font-medium">Quotations &amp; Estimates (with profile auto-fill)</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="font-medium">PDF Export</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="font-medium">Word Document Export</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="font-medium">Predefined tax rates setup (auto-fill)</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="font-medium">Unique direct sharing links for clients</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="font-medium">Client view tracking (read receipts)</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="font-medium">Direct online payment links (Stripe &amp; PayPal)</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="font-medium">Priority email support</span>
              </li>
            </ul>

            {isPro ? (
              <Badge variant="default" className="w-full justify-center py-2">
                <Crown className="h-4 w-4 mr-1" />
                Current Plan
              </Badge>
            ) : (
              <Button 
                className="w-full" 
                size="lg"
                onClick={handleUpgrade}
                disabled={loading}
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Upgrade to Pro
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      {/* FAQ Section */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Frequently Asked Questions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-semibold mb-1">Can I cancel anytime?</h4>
            <p className="text-sm text-muted-foreground">
              Yes, you can cancel your Pro subscription at any time. You'll retain Pro access until 
              the end of your current billing period.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">What happens to my profiles and data if I downgrade?</h4>
            <p className="text-sm text-muted-foreground">
              If you downgrade to Free, you will still have unlimited invoice and quotation creations and PDF downloads. However, you will no longer have access to saved Business, Customer, Inventory & Services, and Predefined Tax profiles (requiring manual entry details for future invoices), direct document sharing links, or Word exports. All your previously saved invoices remain fully accessible.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">Can I get a refund?</h4>
            <p className="text-sm text-muted-foreground">
              We offer a 7-day money-back guarantee for your first Pro subscription purchase. 
              See our{' '}
              <Link to="/refund" className="text-primary hover:underline">
                Refund Policy
              </Link>
              {' '}for complete details.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Payment Provider Selection Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Choose Payment Method</DialogTitle>
            <DialogDescription>
              Select your preferred payment provider to upgrade to Pro
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            <Button
              variant="outline"
              className="w-full h-auto p-4 justify-start"
              onClick={handleStripeCheckout}
              disabled={loading}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded">
                  <CreditCard className="h-6 w-6 text-primary" />
                </div>
                <div className="text-left">
                  <div className="font-semibold">Pay with Stripe</div>
                  <div className="text-sm text-muted-foreground">
                    Credit/Debit Card, Google Pay, Apple Pay
                  </div>
                </div>
              </div>
            </Button>

            <Button
              variant="outline"
              className="w-full h-auto p-4 justify-start"
              onClick={handlePaddleCheckout}
              disabled={loading}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded">
                  <svg className="h-6 w-6 text-emerald-600" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                  </svg>
                </div>
                <div className="text-left">
                  <div className="font-semibold">Pay with Paddle</div>
                  <div className="text-sm text-muted-foreground">
                    Card, PayPal, Google Pay & more
                  </div>
                </div>
              </div>
            </Button>
          </div>
          <p className="text-xs text-muted-foreground text-center mt-4">
            Both payment methods support secure checkout and automatic billing
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
